import { Editor } from '@ckeditor/ckeditor5-core';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Element } from '@ckeditor/ckeditor5-engine';
// eslint-disable-next-line import/no-extraneous-dependencies
import Node from '@ckeditor/ckeditor5-engine/src/model/node';
// eslint-disable-next-line import/no-extraneous-dependencies
// eslint-disable-next-line import/no-extraneous-dependencies
import { createRef, forwardRef, useId, useImperativeHandle, useMemo } from 'react';
import { Draggable } from './Draggable';
import { SectionDataTreeElementNode } from './SectionDataTreeElementNode';
import { UpdateElementHandler } from './SectionDataTreeNodeInterface';
import { SectionDataTreeTextNode } from './SectionDataTreeTextNode';
import { ValidationResults } from './ValidationResults';

interface DataTreeNodeProps {
  editor: Editor;
  element: Node;
  selectedElement: Node | undefined;
  setSelectedElement: (selectedElement: Node | undefined) => void;
  expandingItems: Element[];
  setExpandingItems: (expandingItems: Element[]) => void;
  handleSelectClick: (node: Node, e: React.MouseEvent) => void;
  updateComponent: (ancestor: number) => void;
  ghost: boolean;
  draggingNode: Node | null;
  isDragging: boolean;
  showContextMenu: (node: Node, e: React.MouseEvent) => void;
  viewAttributes: string[];
  scrollIntoElement: Node | undefined;
  resetScrollIntoElement: () => void;
  validationResults: ValidationResults;
}

const SectionDataTreeNode = forwardRef<UpdateElementHandler, DataTreeNodeProps>(
  (
    {
      editor,
      element,
      selectedElement,
      setSelectedElement,
      expandingItems,
      setExpandingItems,
      handleSelectClick,
      updateComponent,
      ghost,
      draggingNode,
      isDragging,
      showContextMenu,
      viewAttributes,
      scrollIntoElement,
      resetScrollIntoElement,
      validationResults,
    },
    ref
  ) => {
    const id = useId();
    const childRef = useMemo(() => createRef<UpdateElementHandler>(), []);

    /**
     * 外部から操作できる関数。
     */
    useImperativeHandle(ref, () => ({
      /**
       * 指定された要素を更新する。
       * @param target 対象要素。
       */
      updateElement: (target: Element) => {
        childRef.current?.updateElement(target);
      },
    }));

    return (
      <div className={ghost ? 'section-data-tree-node-ghost-wrapper' : ''}>
        <Draggable node={element} disabled={editor.isReadOnly} isDragging={draggingNode === element}>
          {element.is('$text') ? (
            <SectionDataTreeTextNode
              key={id}
              element={element}
              selectedElement={selectedElement}
              handleSelectClick={handleSelectClick}
              updateComponent={updateComponent}
              ghost={ghost}
              isDragging={isDragging || draggingNode === element}
              showContextMenu={showContextMenu}
              viewAttributes={viewAttributes}
              validationResults={validationResults}
            />
          ) : (
            <SectionDataTreeElementNode
              ref={childRef}
              key={id}
              editor={editor}
              element={element as Element}
              selectedElement={selectedElement}
              setSelectedElement={setSelectedElement}
              expandingItems={expandingItems}
              setExpandingItems={setExpandingItems}
              handleSelectClick={handleSelectClick}
              updateComponent={updateComponent}
              ghost={ghost}
              draggingNode={draggingNode}
              isDragging={isDragging || draggingNode === element}
              showContextMenu={showContextMenu}
              viewAttributes={viewAttributes}
              scrollIntoElement={scrollIntoElement}
              resetScrollIntoElement={resetScrollIntoElement}
              validationResults={validationResults}
            />
          )}
        </Draggable>
      </div>
    );
  }
);

export { SectionDataTreeNode };
